import type { GoalBmrSettings, GoalHistoryDocType } from '../validators';
import type { Assert, AssertEqual } from './type-assertions';
import type { ExtractDocumentTypeFromTypedRxJsonSchema, RxJsonSchema } from 'rxdb';
import { type GoalHistoryDocTypeV0, goalsHistorySchemaLiteral0 } from './migrations/goalshistory';
import {
  defineRxdbCollectionModel,
  defineRxdbMigration,
  defineRxdbMigrations,
  toRxdbMigrationStrategies,
} from './migration';

// ============================================================================
// RxDB collection schema for goalsHistory.
//
// Document shape mirrors the D1 `goals_history` row. Uses `updatedAtUtc` as
// the checkpoint field (not `updatedAt`).
// ============================================================================

export const GOALS_HISTORY_COLLECTION = 'goalsHistory' as const;

// Backfill value used by the v0→v1 migration for goal docs created before
// `bmrSettings` existed. The user can edit these in the goals UI; the point
// is just to satisfy the now-required field so the doc can sync.
const LEGACY_BMR_SETTINGS_BACKFILL: GoalBmrSettings = {
  heightCm: 170,
  age: 30,
  gender: 'male',
  activityLevel: 'moderately-active',
  formula: 'revised',
  highMuscleContent: false,
  useManual: false,
  manualValue: null,
};

export const goalsHistorySchemaLiteral1 = {
  title: 'goals history schema',
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    userId: { type: 'string', maxLength: 100 },
    effectiveFromDate: { type: 'string', maxLength: 10 },
    goalType: {
      type: 'string',
      enum: ['lose', 'maintain', 'gain', 'fixed'],
    },
    caloriesTarget: { type: 'number' },
    strategyType: {
      type: 'string',
      enum: ['percent', 'grams_per_kg', 'flex'],
    },
    proteinPct: { type: ['number', 'null'] },
    carbsPct: { type: ['number', 'null'] },
    fatPct: { type: ['number', 'null'] },
    proteinGPerKg: { type: ['number', 'null'] },
    carbsGPerKg: { type: ['number', 'null'] },
    fatGPerKg: { type: ['number', 'null'] },
    flexMacro: { type: ['string', 'null'], enum: ['carbs', 'fat', null] },
    fixedProteinG: { type: ['number', 'null'] },
    fixedCarbsG: { type: ['number', 'null'] },
    fixedFatG: { type: ['number', 'null'] },
    bmrSettings: { type: 'object' },
    createdAtUtc: { type: 'string', maxLength: 30 },
    updatedAtUtc: { type: 'string', maxLength: 30 },
    deletedAt: { type: ['string', 'null'] },
  },
  required: ['id', 'userId', 'effectiveFromDate', 'goalType', 'caloriesTarget', 'strategyType', 'proteinPct', 'carbsPct', 'fatPct', 'proteinGPerKg', 'carbsGPerKg', 'fatGPerKg', 'flexMacro', 'fixedProteinG', 'fixedCarbsG', 'fixedFatG', 'bmrSettings', 'createdAtUtc', 'updatedAtUtc', 'deletedAt'],
  indexes: [['userId', 'updatedAtUtc']],
} as const;

// v2: optional per-intensity `workoutFuelingTargets` (g/kg). Additive; existing
// docs simply carry no targets until the user sets them.
export const goalsHistorySchemaLiteral2 = {
  ...goalsHistorySchemaLiteral1,
  version: 2,
  properties: {
    ...goalsHistorySchemaLiteral1.properties,
    workoutFuelingTargets: { type: ['object', 'null'] },
  },
} as const;

export const goalsHistorySchemaLiteral = goalsHistorySchemaLiteral2;
export const GOALS_HISTORY_SCHEMA_VERSION = goalsHistorySchemaLiteral.version;

export interface GoalsHistoryCheckpoint {
  updatedAtUtc: string;
  id: string;
}

type _GoalHistorySchemaDocType = Omit<
  ExtractDocumentTypeFromTypedRxJsonSchema<typeof goalsHistorySchemaLiteral>,
  'bmrSettings' | 'workoutFuelingTargets'
> & {
  bmrSettings: GoalHistoryDocType['bmrSettings'];
  workoutFuelingTargets?: GoalHistoryDocType['workoutFuelingTargets'];
};
type _GoalHistoryDocTypeInSync = Assert<AssertEqual<_GoalHistorySchemaDocType, GoalHistoryDocType>>;

export const goalsHistorySchema: RxJsonSchema<GoalHistoryDocType> = goalsHistorySchemaLiteral;

export const goalsHistoryMigrationSteps = defineRxdbMigrations(
  // v0→v1: backfill `bmrSettings` for docs created before the field existed.
  // Bump `updatedAtUtc` so the next sync push carries the corrected doc to the
  // server (which now NOT NULL-constrains the column).
  defineRxdbMigration<GoalHistoryDocTypeV0, GoalHistoryDocType, 1>(
    1,
    (old) => {
      const hasValidBmr =
        old.bmrSettings !== undefined &&
        old.bmrSettings !== null &&
        typeof old.bmrSettings === 'object' &&
        'heightCm' in old.bmrSettings;
      if (hasValidBmr) {
        return old as GoalHistoryDocType;
      }
      return {
        ...old,
        bmrSettings: { ...LEGACY_BMR_SETTINGS_BACKFILL },
        updatedAtUtc: new Date().toISOString(),
      };
    },
  ),
  // v1→v2: `workoutFuelingTargets` is optional; existing docs pass through
  // unchanged and carry no targets until the user sets them.
  defineRxdbMigration<GoalHistoryDocType, GoalHistoryDocType, 2>(
    2,
    (old) => old,
  ),
);

export const goalsHistoryMigrations = toRxdbMigrationStrategies(goalsHistoryMigrationSteps);

// Re-export the v0 schema literal for tooling that needs to reference prior versions.
export { goalsHistorySchemaLiteral0 };

export const goalsHistoryModel = defineRxdbCollectionModel<typeof GOALS_HISTORY_COLLECTION, GoalHistoryDocType>({
  collection: GOALS_HISTORY_COLLECTION,
  schema: goalsHistorySchema,
  migrationStrategies: goalsHistoryMigrations,
});
